import { logger } from '../utils/logger';
import { retryWithBackoff, isRetryableError } from '../utils/retry';

// Get API base URL
const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_BASE_URL;
  
  if (envUrl) {
    if (envUrl.endsWith('/v1') || envUrl.endsWith('/v1/')) {
      return envUrl.replace(/\/+$/, '');
    }
    if (envUrl === '/api' || envUrl.endsWith('/api')) {
      return `${envUrl.replace(/\/+$/, '')}/v1`;
    }
    if (envUrl.startsWith('http://') || envUrl.startsWith('https://')) {
      if (envUrl.includes('/api')) {
        return `${envUrl.replace(/\/+$/, '')}/v1`;
      }
      return `${envUrl.replace(/\/+$/, '')}/api/v1`;
    }
    return `${envUrl.replace(/\/+$/, '')}/v1`;
  }
  
  return '/api/v1';
};

const API_BASE_URL = getApiBaseUrl();
logger.info(`ProfileService initialized with API_BASE_URL: ${API_BASE_URL}`);

export interface Profile {
  id: string;
  profile_id?: string;
  name: string;
  prompt_type?: string;
  asset_type?: string;
  description?: string;
  version?: string;
  is_default?: boolean;
  category?: string;
  metadata?: Record<string, any>;
  allowed_project_ids?: string[];
}

export interface ProfilesResponse {
  success: boolean;
  profiles: Profile[];
  count?: number;
}

/**
 * Profile Service
 * 
 * Provides API client for fetching AI prompt profiles.
 */
export const profileService = {
  /**
   * Get all available profiles
   */
  async getProfiles(category?: string): Promise<Profile[]> {
    return retryWithBackoff(async () => {
      logger.info('Fetching profiles', { category });
      
      const params = new URLSearchParams();
      if (category) {
        params.append('category', category);
      }
      
      // Backend router is mounted at /api/prompts (not /api/v1/prompt-management)
      const url = `/api/prompts/profiles${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });
      
      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { message: response.statusText || 'Unknown error', status: response.status };
        }
        
        const error = new Error(errorData.detail || errorData.message || 'Failed to get profiles');
        (error as Error & { status?: number }).status = response.status;
        throw error;
      }
      
      const data: ProfilesResponse = await response.json();
      let profiles = data.profiles || [];
      
      // Normalize profiles: ensure 'id' and 'profile_id' are set to the canonical profile_id
      profiles = profiles.map((profile: any) => {
        const resolvedId =
          profile.profile_id || profile.id || profile._id || profile.name;
        return {
          ...profile,
          profile_id: profile.profile_id || resolvedId,
          id: resolvedId,
        };
      });
      
      logger.info('Successfully fetched profiles', { count: profiles.length });
      return profiles;
    }, {
      maxAttempts: 2,
      isRetryable: (error) => {
        if (!isRetryableError(error)) {
          return false;
        }
        const status = (error as Error & { status?: number }).status;
        if (status && status >= 400 && status < 500) {
          return false;
        }
        return true;
      },
    });
  },

  /**
   * Get profiles for a specific project
   */
  async getProfilesForProject(projectId: string): Promise<Profile[]> {
    return retryWithBackoff(async () => {
      logger.info('Fetching profiles for project', { projectId });
      
      // Use the same endpoint as Bulk Indexing for consistency
      // The /api/prompts/profiles endpoint supports project_id as query parameter
      const url = `/api/prompts/profiles${projectId ? `?project_id=${projectId}` : ''}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });
      
      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { message: response.statusText || 'Unknown error', status: response.status };
        }
        
        const error = new Error(errorData.detail || errorData.message || 'Failed to get profiles for project');
        (error as Error & { status?: number }).status = response.status;
        throw error;
      }
      
      const data = await response.json();
      let profiles = data.profiles || [];
      
      profiles = profiles.map((profile: any) => {
        const resolvedId =
          profile.profile_id || profile.id || profile._id || profile.name;
        return {
          ...profile,
          profile_id: profile.profile_id || resolvedId,
          id: resolvedId,
        };
      });
      
      logger.info('Successfully fetched profiles for project', { projectId, count: profiles.length });
      return profiles;
    }, {
      maxAttempts: 2,
      isRetryable: (error) => {
        if (!isRetryableError(error)) {
          return false;
        }
        const status = (error as Error & { status?: number }).status;
        if (status && status >= 400 && status < 500) {
          return false;
        }
        return true;
      },
    });
  },
};

